/**
 * The in-app player's loader: a member previewing an asset they can open.
 *
 * `asset_playback_open` hands over a `playback_token` and the asset read supplies what the player
 * lists — the asset itself, or a collection's entries — so everything after it is the `visitor`
 * playback routes, exactly as on an embed page. `asset_player_read` adds what a publication of the
 * asset would look like and show: the resolved skin and the switched-on cards, so a member sees
 * the preset and the overlays they configured. The token names no publication, so there is no
 * advertising, no recommendations strip and no source download through the player: a member
 * downloads through the library's own signed link. And it reaches the asset it was minted for and
 * nothing past it, so a collection's entries each get a token of their own.
 */

import { type Ref } from 'vue'
import { ApiError } from '@/api/client'
import { getAsset, type AssetSummary } from '@/api/assets/assets'
import { createDownload } from '@/api/assets/downloads'
import { getCollection, listCollectionItems } from '@/api/assets/collections'
import { listMetadataValues } from '@/api/assets/metadata'
import { getAssetPlayer, openAssetPlayback, type AssetPlayerView } from '@/api/assets/playback'
import { createUUID } from '../../types/base'
import type { EmbedItem, OverlayConfig, PlayerTheme } from '../../types/embed'
import type { AssetListItem, PublicMetadata } from '../../types/asset'
import {
  toAssetListItem,
  toCollectionSubtype,
  transformOverlay,
  transformPlayerTheme,
} from '../../types/transforms'
import { useEmbedLoader, type UseEmbedLoaderReturn } from './useEmbedLoader'
import type { EmbedLoaderApiFn } from './usePlayerContext'

/**
 * Where an asset's poster is, or nothing for an asset without one.
 *
 * The signed string the asset read hands over, never a path built from `thumbnail_file_id`: the byte
 * route refuses an unsigned request for a still. It lives about a day, which is why this is read off
 * the payload each time a loader runs rather than remembered anywhere.
 */
function posterOf(summary: AssetSummary): string | undefined {
  return summary.thumbnail_url ?? undefined
}

/** One asset of the library as a player lists it. */
function listItem(
  summary: AssetSummary,
  description: string | undefined,
  publicMetadata: PublicMetadata[] | undefined,
): AssetListItem | undefined {
  return toAssetListItem({
    uid: summary.uid,
    name: summary.name,
    type: summary.type,
    description,
    thumbnailUrl: posterOf(summary),
    duration: summary.duration_ms == null ? undefined : summary.duration_ms / 1000,
    publicMetadata,
  })
}

/** How many rows one value read may name — `metadata_values_list`'s own cap. */
const VALUES_PER_READ = 500

/**
 * Each asset's public custom fields, displayed: what an embed of it lists under its description,
 * so a member previewing an asset sees the panel a viewer will. The member's own value read, kept to
 * the fields marked public and the values that say something — the embed's rule.
 */
async function publicMetadataOf(assetIds: string[]): Promise<Map<string, PublicMetadata[]>> {
  const chunks: string[][] = []
  for (let start = 0; start < assetIds.length; start += VALUES_PER_READ) {
    chunks.push(assetIds.slice(start, start + VALUES_PER_READ))
  }
  const reads = await Promise.all(chunks.map((ids) => listMetadataValues({ ids: ids.join(',') })))
  const shown = new Map<string, PublicMetadata[]>()
  for (const value of reads.flat()) {
    if (!value.field_is_public || !value.display_value) continue
    const fields = shown.get(value.target_id) ?? []
    fields.push({ label: value.field_label, value: value.display_value })
    shown.set(value.target_id, fields)
  }
  return shown
}

/** Milliseconds to the seconds a player's clip is expressed in. */
function seconds(milliseconds: number | null | undefined): number | undefined {
  return milliseconds == null ? undefined : milliseconds / 1000
}

/**
 * One entry's token, minted when the player first asks about that entry.
 *
 * The playback routes hold no session, so they cannot check this member's seat on an entry's shelf;
 * `asset_playback_open` does, entry by entry. Lazily rather than for every entry up front, because
 * the list renders from the collection read and only playing or prefetching an entry needs its
 * token. An entry this member cannot open answers `not_found` and yields no token: the player gets
 * nothing to play for it, and the other entries play on.
 */
async function entryToken(assetUid: string): Promise<string | undefined> {
  try {
    return (await openAssetPlayback(assetUid)).playback_token
  } catch (error) {
    if (error instanceof ApiError && error.code === 'not_found') return undefined
    throw error
  }
}

/**
 * One entry's source link, minted by `app` for this member: the playback token reaches no download
 * route, because it names no publication. Minted when the viewer asks, since the link expires.
 */
async function entrySourceDownload(assetUid: string, viaAssetId?: string): Promise<string | undefined> {
  try {
    return (await createDownload({ asset_id: assetUid, via_asset_id: viaAssetId })).url
  } catch {
    return undefined
  }
}

/**
 * The skin and the cards a publication of the asset would carry, in the player's shapes.
 *
 * **A failure of this read plays the asset without them** rather than failing the player: the look
 * is decoration over what the member opened, and the token read beside it is what says whether
 * they may play it at all.
 */
async function lookOf(
  assetId: string,
  preset?: string,
): Promise<{ playerTheme?: PlayerTheme; overlays: OverlayConfig[] }> {
  let view: AssetPlayerView
  try {
    view = await getAssetPlayer(assetId, undefined, preset)
  } catch {
    return { overlays: [] }
  }
  return {
    playerTheme: transformPlayerTheme(view.player_theme),
    // The visitor surface's card, less the strip's videos, which this player never carries.
    overlays: view.overlays.flatMap((card) => transformOverlay({ ...card, recommended_assets: [] })),
  }
}

/**
 * Load one asset's player in the application: `EmbedLoaderApiFn`'s in-app arm.
 *
 * A collection plays its entries, in the collection's own order, each through a token of its own
 * ({@link entryToken}): the collection's token reaches the collection and none of its entries.
 *
 * `viaAssetId` previews a file linked to an asset the member reads in the organization's pool,
 * though the file sits on a shelf they do not see: the token, the read and the source link are asked
 * through that asset. The look and the public fields are not — they are decoration, and their reads
 * answer nothing for such a file — so it plays with the defaults.
 *
 * `subtitlesAutoStart` starts the asset's default track, which only the asset's preview asks for
 * (`AssetPlayer`'s prop of that name).
 */
export const loadAppPlayer = async (
  assetId: string,
  params: Parameters<EmbedLoaderApiFn>[1],
  viaAssetId?: string,
  subtitlesAutoStart = false,
): ReturnType<EmbedLoaderApiFn> => {
  const [opened, asset, look] = await Promise.all([
    openAssetPlayback(assetId, viaAssetId),
    getAsset(assetId, undefined, viaAssetId),
    // A preview of a preset — the Developer tab's — draws that preset's look (ER11).
    lookOf(assetId, params.preset),
  ])
  const description = asset.description || undefined
  const base = {
    uid: createUUID(asset.uid),
    name: asset.name,
    description,
    thumbnailUrl: posterOf(asset),
    overlays: look.overlays,
    playerTheme: look.playerTheme,
    playbackToken: opened.playback_token,
    sourceDownload: (assetUid: string) => entrySourceDownload(assetUid, viaAssetId),
    // The preview starts on the asset's default track, as a publication by its own uid does
    // (`subtitles_auto_start`): the subtitles tab's "at start" is what the member previews. The
    // working players — trimming, a thumbnail capture, Scribe's editor, which draws its own
    // cues, an overlay being placed, a settings or embed preview — leave it off.
    ...(subtitlesAutoStart && { playerConfig: { subtitlesDefaultMode: 'specific' as const, subtitlesAutoStart } }),
  }

  if (asset.type !== 'collection') {
    const shown = await publicMetadataOf([asset.uid])
    const item = listItem(asset, description, shown.get(asset.uid))
    return { ...base, embedType: 'single', items: item ? [{ asset: item, position: 0 }] : [] }
  }

  const [collection, entries] = await Promise.all([getCollection(assetId), listCollectionItems(assetId)])
  const shown = await publicMetadataOf(entries.map((entry) => entry.uid))
  const items = entries.flatMap((entry, index): EmbedItem[] => {
    const item = listItem(entry, entry.excerpt || undefined, shown.get(entry.uid))
    if (!item) return []
    const start = seconds(entry.start_ms)
    const end = seconds(entry.end_ms)
    return [
      {
        asset: item,
        position: entry.position ?? index,
        customTitle: entry.custom_title ?? undefined,
        customDescription: entry.custom_description ?? undefined,
        clip: start !== undefined || end !== undefined ? { start: start ?? 0, end: end ?? Infinity } : undefined,
      },
    ]
  })
  const subtype = toCollectionSubtype(collection.subtype)
  return {
    ...base,
    embedType: 'collection',
    items,
    collection: subtype ? { subtype, totalItems: items.length } : undefined,
    itemToken: entryToken,
  }
}

export interface UseAssetPlayerOptions {
  /** Asset ID to load */
  assetId: Ref<string | undefined>
  /** Auto-load when assetId changes (default: true) */
  autoLoad?: boolean
}

/**
 * Load player data for an asset in the application.
 *
 * Returns the same interface as `useEmbedLoader` so it can be used
 * interchangeably with embed-based loading.
 *
 * @example
 * ```typescript
 * const { embed, isLoading, error } = useAssetPlayer({
 *   assetId: toRef(props, 'assetId'),
 * })
 * ```
 */
export function useAssetPlayer<T extends AssetListItem = AssetListItem>(
  options: UseAssetPlayerOptions,
): UseEmbedLoaderReturn<T> {
  const { assetId, autoLoad = true } = options

  return useEmbedLoader<T>({
    embedId: assetId,
    autoLoad,
    apiFn: loadAppPlayer,
    audience: 'member',
  })
}
